import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { formatShort } from "../lib/format";
import { gameTableQuery } from "../lib/queries";

export const Route = createFileRoute("/rules")({ component: RulesPage });

const TABLES = [
	{ name: "character", label: "Character and combat", blurb: "Base stats, the damage formula, crits, in-run levels, meta upgrade costs." },
	{ name: "economy", label: "Economy", blurb: "Energy, coins per kill, drops, the chapter chest, gacha rates." },
	{ name: "run_structure", label: "Run structure", blurb: "The chapter template every chapter merges over, and the interaction rules." },
	{ name: "gear", label: "Gear rules", blurb: "Rarity multipliers and the slot-level curve." },
	{ name: "skills", label: "Skill rules", blurb: "Offer rates, level multipliers, statuses, ultimates." },
	{ name: "perks", label: "Perk rules", blurb: "Which effect types and hooks exist." },
	{ name: "enemies", label: "Enemy scaling", blurb: "Per-stage and per-chapter multipliers, boss multipliers." },
	{ name: "pets", label: "Pets", blurb: "Pet level curve and the starter pet." },
] as const;

/**
 * The rules blocks straight from the game's JSON, rendered as nested
 * definition lists. This page exists so nobody has to open the repo to check a
 * number; it is deliberately not editable here.
 */
function RulesPage() {
	const [active, setActive] = useState<string>(TABLES[0].name);
	const table = useQuery(gameTableQuery(active));
	const current = TABLES.find((t) => t.name === active)!;

	return (
		<div>
			<PageHeader title="Rules and numbers" eyebrow="Straight from data/*.json, read-only" />
			<div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
				<nav className="flex flex-row flex-wrap gap-1 lg:flex-col" aria-label="Rule tables">
					{TABLES.map((t) => (
						<button
							key={t.name}
							type="button"
							onClick={() => setActive(t.name)}
							className="rounded-md px-3 py-2 text-left text-sm transition-colors"
							style={active === t.name ? { background: "var(--color-ink-800)", color: "var(--color-gold-400)" } : undefined}
						>
							{t.label}
						</button>
					))}
				</nav>
				<section className="panel p-5">
					<h2 className="text-lg">{current.label}</h2>
					<p className="mb-4 text-sm" style={{ color: "var(--color-ink-300)" }}>
						{current.blurb}
					</p>
					{table.isPending && <Loading />}
					{table.isError && <LoadError error={table.error} retry={() => void table.refetch()} />}
					{table.data && (
						<>
							<Tree value={table.data.data} />
							<p className="mt-4 text-xs" style={{ color: "var(--color-ink-400)" }}>
								Imported {formatShort(table.data.imported_at)}.
							</p>
						</>
					)}
				</section>
			</div>
		</div>
	);
}

function Tree({ value, depth = 0 }: { value: unknown; depth?: number }) {
	if (value === null || typeof value !== "object") {
		return <span className="num font-mono text-sm">{String(value)}</span>;
	}
	if (Array.isArray(value)) {
		const scalar = value.every((v) => v === null || typeof v !== "object");
		if (scalar) return <span className="font-mono text-sm">{value.map(String).join(", ")}</span>;
		return (
			<ol className="space-y-1">
				{value.map((v, i) => (
					<li key={i} className="rounded p-2" style={{ background: "var(--color-ink-800)" }}>
						<Tree value={v} depth={depth + 1} />
					</li>
				))}
			</ol>
		);
	}
	return (
		<dl className={depth === 0 ? "space-y-3" : "space-y-1"}>
			{Object.entries(value).map(([k, v]) => {
				const nested = v !== null && typeof v === "object" && !(Array.isArray(v) && v.every((x) => typeof x !== "object"));
				return (
					<div key={k} className={nested ? "" : "grid grid-cols-[minmax(10rem,14rem)_1fr] gap-3"}>
						<dt className={`font-mono text-xs ${depth === 0 && nested ? "mt-2 text-sm font-semibold" : ""}`} style={{ color: depth === 0 && nested ? "var(--color-ink-100)" : "var(--color-ink-300)" }}>
							{k}
						</dt>
						<dd className={nested ? "mt-1 border-l pl-3" : ""} style={nested ? { borderColor: "var(--color-ink-700)" } : undefined}>
							<Tree value={v} depth={depth + 1} />
						</dd>
					</div>
				);
			})}
		</dl>
	);
}
